// CB:BEGIN referral-landing-page
import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../lib/getApiUrl';
import { useDevice } from '../hooks/useDevice';
import { CheckCircle2, Home, ArrowRight } from 'lucide-react';

const BRAND_GREEN = '#123c3b';

interface ReferralLandingPageProps {
  code: string;
  onNavigate: (screen: string) => void;
}

const ReferralLandingPage: React.FC<ReferralLandingPageProps> = ({ code, onNavigate }) => {
  const { isMobileLayout } = useDevice();
  const [state, setState] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const [realtorName, setRealtorName] = useState('');

  useEffect(() => {
    if (!code) { setState('invalid'); return; }
    const upper = code.trim().toUpperCase();

    const validate = async () => {
      try {
        const base = getApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${base}/api/realtor/public/${upper}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.valid) { setState('invalid'); return; }

        setRealtorName(data.brokerageName || data.realtorName || 'Your Realtor');
        setState('valid');

        // Persist referral code for attribution through signup
        try {
          sessionStorage.setItem('cb.referral.code', upper);
          localStorage.setItem('cb.referral.code', upper);
        } catch {}

        // Track click (fire-and-forget)
        const sessionId = (() => { try { return sessionStorage.getItem('cb.session.id') || undefined; } catch { return undefined; } })();
        fetch(`${base}/api/referrals/track-click`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ referralCode: upper, sessionId }),
        }).catch(() => {});
      } catch {
        setState('invalid');
      }
    };
    validate();
  }, [code]);

  const handleGetStarted = () => {
    // Pass realtor referral code into the homeowner signup flow
    try { sessionStorage.setItem('cb.referral.code', code.trim().toUpperCase()); } catch {}
    onNavigate('landing');
  };

  if (state === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <span style={{ color: '#64748b', fontSize: 16 }}>Loading…</span>
      </div>
    );
  }

  if (state === 'invalid') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', padding: 40, maxWidth: 440 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>Referral link not found</h2>
          <p style={{ color: '#64748b', fontSize: 16, lineHeight: 1.6, marginBottom: 32 }}>
            This referral link may be expired or invalid. You can still get roofing quotes — just start below.
          </p>
          <button
            onClick={() => onNavigate('landing')}
            style={{ padding: '14px 32px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            Go to CoreBids <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <div style={{ background: BRAND_GREEN, padding: isMobileLayout ? '16px 20px' : '20px 40px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: '#fff', cursor: 'pointer' }} onClick={() => onNavigate('landing')}>
          CoreBids
        </span>
      </div>

      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #123c3b 0%, #0f5251 50%, #1a6b69 100%)',
        padding: isMobileLayout ? '56px 20px 72px' : '80px 40px 100px',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 99, padding: '6px 16px', fontSize: 13, color: 'rgba(255,255,255,0.9)', marginBottom: 28 }}>
            <CheckCircle2 size={14} style={{ color: '#a7f3d0' }} />
            Sent by {realtorName}
          </div>

          <h1 style={{ fontSize: isMobileLayout ? 36 : 52, fontWeight: 900, color: '#fff', marginBottom: 20, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
            Get Roof Replacement Quotes for Your Home
          </h1>

          <p style={{ fontSize: isMobileLayout ? 17 : 20, color: 'rgba(255,255,255,0.88)', lineHeight: 1.6, maxWidth: 540, margin: '0 auto 40px' }}>
            Your realtor has arranged for you to get up to 3 competitive bids — reviewed, compared, and ready for you to accept.
          </p>

          <button
            onClick={handleGetStarted}
            style={{
              fontSize: isMobileLayout ? 17 : 20,
              fontWeight: 700,
              padding: isMobileLayout ? '16px 28px' : '20px 52px',
              background: '#fff',
              color: BRAND_GREEN,
              border: 'none',
              borderRadius: 12,
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            Get My Quotes <ArrowRight size={20} />
          </button>

          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 14 }}>Free to use · No obligation · Up to 3 bids</p>
        </div>
      </section>

      {/* How it works */}
      <section style={{ padding: isMobileLayout ? '56px 20px' : '80px 40px', background: '#fff' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobileLayout ? 28 : 36, fontWeight: 900, color: '#0f172a', textAlign: 'center', marginBottom: 48 }}>
            Here's how it works
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : 'repeat(3, 1fr)', gap: 28 }}>
            {[
              { num: '1', icon: <Home size={28} style={{ color: BRAND_GREEN }} />, title: 'Create your account', desc: 'Sign up as a homeowner in under 2 minutes. Your referral from ' + realtorName + ' is already linked.' },
              { num: '2', icon: <CheckCircle2 size={28} style={{ color: BRAND_GREEN }} />, title: 'Review your bids', desc: 'Contractors in your area compete for your project. You get up to 3 real bids — not ballparks.' },
              { num: '3', icon: <ArrowRight size={28} style={{ color: BRAND_GREEN }} />, title: 'Accept and get started', desc: 'Pick the bid you want and confirm. Payment is held in escrow — released only when work is complete.' },
            ].map(s => (
              <div key={s.num} style={{ background: '#f8fafc', borderRadius: 16, padding: 28, border: '2px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: BRAND_GREEN, color: '#fff', fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.num}</div>
                  {s.icon}
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{ padding: isMobileLayout ? '56px 20px' : '72px 40px', background: 'linear-gradient(135deg, #123c3b 0%, #0f5251 100%)', textAlign: 'center' }}>
        <div style={{ maxWidth: 540, margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobileLayout ? 28 : 36, fontWeight: 900, color: '#fff', marginBottom: 16, lineHeight: 1.2 }}>
            Ready to get your quotes?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16, marginBottom: 32 }}>
            {realtorName} is already set up to help you through the process.
          </p>
          <button
            onClick={handleGetStarted}
            style={{ padding: isMobileLayout ? '14px 28px' : '18px 48px', background: '#fff', color: BRAND_GREEN, border: 'none', borderRadius: 12, fontSize: 17, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
          >
            Get Started Free <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default ReferralLandingPage;
// CB:END referral-landing-page
